import { apiClient } from './api'

// ===== TYPES =====

// Prequalification invite status types
export type PrequalStatus = 'PENDING' | 'SENT' | 'COMPLETED' | 'DECLINED' | 'EXPIRED'

export type ConversationStatus = 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'ABANDONED' | 'TRANSFERRED'

/** Set by API for TRANSFERRED rows; derived from context_data.escalationSource */
export type ConversationStopCategory = 'NONE' | 'CRM_STOP' | 'UNREACHABLE' | 'ESCALATED'

/** Enriched vehicle-of-interest details for tooltip display */
export interface VehicleInterestDetails {
  year?: number
  make?: string
  model?: string
  trim?: string
  stockNumber?: string
  vin?: string
  mileage?: number
  exteriorColor?: string
  interiorColor?: string
  listedPrice?: number
  vehicleType?: string
  description?: string
  style?: string
  transmission?: string
  drivetrain?: string
  engine?: string
  engineType?: string
  vehicleClass?: string
  bodyType?: string
  fuelType?: string
  equipment?: string
  features?: string[]
}

/** CRM touchpoints from `customer_external_events` (embedded on customer via `/customers/conversations`) */
export interface CustomerExternalEvent {
  id: string
  createdAt: string
  updatedAt: string
  dealershipId: string
  customerId: string
  eventProvider: string
  integrationConnector: string
  externalId: string
  category: string
  occurredAt: string | null
  ingestedAt: string
  subject: string | null
  body: string | null
  direction: string | null
  metadata: Record<string, unknown> | null
  /** Provider TCPA consent from Lead ADF body (Lead communication rows only) */
  providerLeadConsent?: 'granted' | 'not_granted' | 'missing'
}

export type UnreachableChannel = 'SMS' | 'EMAIL'

export interface Conversation {
  id: string
  dealershipId: string
  customerId: string
  customerName: string
  customerPhone?: string
  customerEmail?: string
  channel: string
  status: ConversationStatus
  stopCategory?: ConversationStopCategory
  /** Present for TRANSFERRED conversations — e.g. no_valid_channel, consent_not_granted */
  escalationSource?: string
  /** Channels the system has marked undeliverable for this conversation */
  unreachableChannels?: UnreachableChannel[]
  currentWorkflow?: string
  workflowStep?: string
  vehicleInterest?: string
  vehicleInterestDetails?: VehicleInterestDetails
  leadSource?: string
  appointmentStatus?: string
  aiActive: boolean
  agentAssigned?: string
  startedAt: string
  lastMessageAt: string
  messageCount: number
  messages?: Message[]
  // Summary fields
  conversationSummary?: string
  workflowSummary?: any
  summaryGeneratedAt?: string
  summaryPostedToCrm?: boolean
  summaryPostedAt?: string
  // Prequalification fields
  prequalStatus?: PrequalStatus
  /** Present when loaded from `/customers/conversations` (newest-first slice from API) */
  customerExternalEvents?: CustomerExternalEvent[]
}

export interface Message {
  id: string
  conversationId: string
  role: string
  content: string
  channel: string
  direction?: 'inbound' | 'outbound'
  status?: string
  timestamp: string
  processedAt?: string | null
  mediaUrls?: string[] // For MMS media attachments
  metadata?: {
    aiGenerated?: boolean
    crmUpdated?: boolean
    messageType?: string
    deliveryBlocked?: boolean
    blockReason?: string
    blockReasonLabel?: string
    blockReasonCode?: string
    twilioStatus?: string
    twilioErrorCode?: string
    systemGenerated?: boolean
    escalationReason?: string
    escalationCategory?: string
    /** A message between dealership staff and the customer, from the CRM — not part of BDC AI's conversation. */
    staffMessage?: boolean
  }
}

// ===== API FUNCTIONS =====

/**
 * Get all conversations for a dealership by fetching customers
 */
export interface Appointment {
  id: string
  customerId: string
  type: string
  scheduledAt: string
  duration: number
  dealershipId?: string
  salesRepId?: string
  status: 'SCHEDULED' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW'
  notes?: string
  recoveryContext?: any // JSON field that may contain sale information (e.g., { type: 'SOLD', saleAmount: number })
  createdAt: string
  updatedAt: string
}

export interface Lead {
  id: string
  customerName: string
  customerPhone?: string
  customerEmail?: string
  leadSource?: string
  vehicle?: string
  appointmentTime?: string
  appointmentStatus?:
    | 'SCHEDULED'
    | 'CONFIRMED'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'CANCELLED'
    | 'NO_SHOW'
    | 'MISSED_RECOVERY_PENDING'
    | 'MISSED_RECOVERY_IN_PROGRESS'
    | 'SHOWN_UNSOLD'
    | 'UNSOLD_RECOVERY_PENDING'
    | 'UNSOLD_RECOVERY_IN_PROGRESS'
    | 'RESCHEDULED'
    | 'ABANDONED'
  totalMessages?: number
  value?: number
  isSold?: boolean // True if recoveryContext.type === 'SOLD'
  conversationId?: string
  customerId?: string
  createdAt: string
  /** From appointment list: AI vs CRM create path */
  appointmentSource?: 'AI_ASSISTANT' | 'CRM_SYNC' | null
}

export enum CustomerSortField {
  UPDATED_AT = 'updatedAt',
  CREATED_AT = 'createdAt',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export interface GetConversationsOptions {
  limit?: number
  status?: string
  sortBy?: CustomerSortField
  sortOrder?: SortOrder
  hasAppointments?: boolean
  includeAppointments?: boolean
  searchQuery?: string
}

export async function getConversations(
  dealershipId: string,
  options?: GetConversationsOptions,
): Promise<{ conversations: Conversation[]; total: number; totalActive?: number }> {
  try {
    // Build query parameters for the optimized conversations endpoint
    const params = new URLSearchParams()
    params.append('dealershipId', dealershipId)

    // Add sorting parameters - default to lastActivity desc (most recent first)
    // Map CustomerSortField to conversation sort field
    const sortByMap: Record<CustomerSortField, 'updatedAt' | 'createdAt' | 'lastActivity'> = {
      [CustomerSortField.UPDATED_AT]: 'lastActivity',
      [CustomerSortField.CREATED_AT]: 'createdAt',
      [CustomerSortField.FIRST_NAME]: 'lastActivity', // Fallback for unsupported fields
      [CustomerSortField.LAST_NAME]: 'lastActivity', // Fallback for unsupported fields
    }
    const sortBy = sortByMap[options?.sortBy || CustomerSortField.UPDATED_AT] || 'lastActivity'
    const sortOrder = options?.sortOrder || SortOrder.DESC
    params.append('sortBy', sortBy)
    params.append('sortOrder', sortOrder)

    // Add hasAppointments filter if specified
    if (options?.hasAppointments !== undefined) {
      params.append('hasAppointments', options.hasAppointments.toString())
    }

    // Add includeAppointments flag if specified
    if (options?.includeAppointments !== undefined) {
      params.append('includeAppointments', options.includeAppointments.toString())
    }

    // Add conversation status filter (e.g. ACTIVE, PAUSED, TRANSFERRED)
    if (options?.status) {
      params.append('status', options.status)
    }

    // Add pagination
    if (options?.limit) {
      params.append('limit', options.limit.toString())
    }
    // Skip is handled by the limit parameter in the frontend pagination logic

    // Parse searchQuery and add search parameters
    if (options?.searchQuery) {
      const query = options.searchQuery.trim()

      // Check if it's an email
      if (query.includes('@')) {
        params.append('email', query)
      } else {
        // Treat as name search
        const nameParts = query.split(/\s+/)
        if (nameParts.length >= 2) {
          // Multiple words: search by first and last name (AND logic)
          params.append('firstName', nameParts[0])
          params.append('lastName', nameParts.slice(1).join(' '))
        } else {
          // Single word: search by lastName
          // Backend will use OR logic to search both firstName and lastName
          params.append('lastName', query)
        }
      }
    }

    // Use the new optimized conversations endpoint
    const url = `/customers/conversations?${params.toString()}`

    let response: { conversations: any[]; total: number; totalActive?: number } | null = null
    try {
      const rawResponse = await apiClient.get<any>(url)
      response = rawResponse as { conversations: any[]; total: number; totalActive?: number }
    } catch (apiError: any) {
      // Log detailed error information
      const errorDetails = {
        message: apiError.message,
        status: apiError.response?.status,
        statusText: apiError.response?.statusText,
        data: apiError.response?.data,
        url,
      }
      console.error('API call to conversations endpoint failed:', errorDetails)

      // Re-throw with more context
      throw new Error(`Failed to fetch conversations: ${apiError.response?.status || 'Unknown error'} - ${apiError.message}`)
    }

    // Handle null/undefined response
    if (!response) {
      console.error('API returned null/undefined response for URL:', url)
      return {
        conversations: [],
        total: 0,
        totalActive: 0,
      }
    }

    // Validate response structure - handle both direct array and object with conversations property
    if (Array.isArray(response)) {
      // If response is directly an array, wrap it
      response = {
        conversations: response,
        total: response.length,
        totalActive: undefined,
      }
    } else if (!response.conversations) {
      console.error('Invalid response structure - missing conversations array:', response)
      return {
        conversations: [],
        total: response.total || 0,
        totalActive: response.totalActive,
      }
    }

    const conversations: Conversation[] = (response.conversations || []).map(conv =>
      mapConversationFromApi(conv, dealershipId, options?.includeAppointments),
    )

    return {
      conversations,
      total: response.total || 0,
      totalActive: response.totalActive,
    }
  } catch (error: any) {
    console.error('Failed to fetch conversations:', error)
    throw new Error(error.response?.data?.error || error.message || 'Failed to fetch conversations')
  }
}

export interface GetConversationOptions {
  customerId?: string
  customerName?: string
  customerPhone?: string
  customerEmail?: string
}

function buildCustomerStubFromOptions(customerId: string, options?: GetConversationOptions) {
  const name = options?.customerName?.trim()
  let firstName: string | undefined
  let lastName: string | undefined
  if (name) {
    const spaceIdx = name.indexOf(' ')
    if (spaceIdx > 0) {
      firstName = name.slice(0, spaceIdx)
      lastName = name.slice(spaceIdx + 1).trim() || undefined
    } else {
      firstName = name
    }
  }
  return {
    id: customerId,
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    phone: options?.customerPhone,
    email: options?.customerEmail,
  }
}

export function conversationFetchOptions(
  conversation: Pick<Conversation, 'customerId' | 'customerName' | 'customerPhone' | 'customerEmail'>,
): GetConversationOptions {
  return {
    customerId: conversation.customerId,
    customerName: conversation.customerName,
    customerPhone: conversation.customerPhone,
    customerEmail: conversation.customerEmail,
  }
}

async function loadConversationForCustomer(
  dealershipId: string,
  customerId: string,
  conversationId: string,
  customerHint?: GetConversationOptions,
): Promise<Conversation> {
  const response = await apiClient.get<any[]>(`/customers/${customerId}/conversations?dealershipId=${dealershipId}`)
  const conversations = response || []
  const conv = conversations.find(c => c.id === conversationId)
  if (!conv) {
    throw new Error(`Conversation ${conversationId} not found for customer ${customerId}`)
  }

  const customer = buildCustomerStubFromOptions(customerId, customerHint)
  const conversation = mapConversationFromApi({ ...conv, customer }, dealershipId, false)
  const messages = mapMessagesFromApi(conv.messages || [], conv.id, conv.channel)

  return {
    ...conversation,
    messageCount: messages.length,
    messages: withStaffMessages(messages, conv.staffMessages, conv.id),
  }
}

export async function getConversation(
  dealershipId: string,
  conversationId: string,
  options?: GetConversationOptions,
): Promise<Conversation> {
  try {
    if (options?.customerId) {
      return await loadConversationForCustomer(dealershipId, options.customerId, conversationId, options)
    }

    // First, try to find the conversation using the optimized conversations endpoint
    // We'll fetch the maximum allowed batch (100) to increase chances of finding it
    let targetConversation: any = null
    let targetCustomer: any = null

    const conversationsResponse = await apiClient.get<{ conversations: any[]; total: number }>(
      `/customers/conversations?dealershipId=${dealershipId}&limit=100`,
    )

    if (conversationsResponse?.conversations) {
      targetConversation = conversationsResponse.conversations.find(c => c.id === conversationId)
      if (targetConversation) {
        targetCustomer = targetConversation.customer
      }
    }

    // If not found in initial batch, try paginated search
    if (!targetConversation || !targetCustomer) {
      // Try to get the conversation directly by searching through customer conversations
      // This is still inefficient but better than loading all customers first
      // We'll need to iterate, but let's try a different approach - use the conversations endpoint
      // with pagination to find it

      // Actually, the best approach is to fetch the conversation's customer ID from the backend
      // For now, let's search through paginated results
      let found = false
      let skip = 0
      const limit = 100

      while (!found && skip < 1000) {
        // Limit search to first 1000 conversations to avoid infinite loop
        try {
          const searchResponse = await apiClient.get<{ conversations: any[]; total: number }>(
            `/customers/conversations?dealershipId=${dealershipId}&limit=${limit}&skip=${skip}`,
          )

          if (searchResponse?.conversations) {
            targetConversation = searchResponse.conversations.find(c => c.id === conversationId)
            if (targetConversation) {
              targetCustomer = targetConversation.customer
              found = true
              break
            }

            // If we've checked all conversations, stop
            if (searchResponse.conversations.length < limit || skip + limit >= searchResponse.total) {
              break
            }
          }

          skip += limit
        } catch (err) {
          console.error(`Failed to search conversations at offset ${skip}:`, err)
          break
        }
      }
    }

    // If still not found, throw error
    if (!targetConversation || !targetCustomer) {
      throw new Error(`Conversation ${conversationId} not found in dealership ${dealershipId}`)
    }

    // We have the conversation and customer from the search, but we need the full conversation
    // with messages. Fetch it from the customer's conversations endpoint
    const customerId = targetConversation.customerId || targetCustomer.id
    const response = await apiClient.get<any[]>(`/customers/${customerId}/conversations?dealershipId=${dealershipId}`)
    const conversations = response || []

    const conv = conversations.find(c => c.id === conversationId)
    if (!conv) {
      console.error(`Conversation ${conversationId} found in search but not in customer conversations list`)
      throw new Error('Conversation not found')
    }

    // Use customer data from the conversation or the fetched customer
    const customer = targetCustomer || (targetConversation?.customer ? targetConversation.customer : null)
    if (!customer) {
      throw new Error('Customer data not found for conversation')
    }

    // Use the shared mapping function, then add messages
    const conversation = mapConversationFromApi({ ...conv, customer }, dealershipId, false)
    const messages = mapMessagesFromApi(conv.messages || [], conv.id, conv.channel)

    return {
      ...conversation,
      messageCount: messages.length,
      messages: withStaffMessages(messages, conv.staffMessages, conv.id),
    }
  } catch (error: any) {
    console.error('Failed to fetch conversation:', error)
    throw new Error(error.response?.data?.error || error.message || 'Failed to fetch conversation')
  }
}

/**
 * Send a message in a conversation
 */
export async function sendMessage(
  dealershipId: string,
  conversationId: string,
  content: string,
  options?: { simulateUser?: boolean; channel?: string },
): Promise<{
  success: boolean
  messages?: Message[]
  updatedState?: any
  workflowType?: string
  nextAction?: string
}> {
  try {
    // This would use the workflow process endpoint
    const response = await apiClient.post<{
      success: boolean
      messages?: any[]
      updatedState?: any
      workflowType?: string
      nextAction?: string
    }>(`/workflows/process`, {
      content,
      conversationId,
      channel: options?.channel || 'SMS', // Default to SMS if not provided
      metadata: {
        dealershipId,
        sendRealResponses: false,
        simulateUser: options?.simulateUser || false,
      },
    })

    // Map messages using helper function
    const mappedMessages = mapMessagesFromApi(response.messages || [], conversationId, options?.channel)

    return {
      success: response.success,
      messages: mappedMessages,
      updatedState: response.updatedState,
      workflowType: response.workflowType,
      nextAction: response.nextAction,
    }
  } catch (error: any) {
    console.error('Failed to send message:', error)
    throw new Error(error.response?.data?.error || error.message || 'Failed to send message')
  }
}

/**
 * Pause all AI communication for a customer (all conversations). Reversible via restart.
 */
export async function pauseCustomerCommunication(
  dealershipId: string,
  customerId: string,
): Promise<{ success: boolean; conversationsAffected?: number }> {
  try {
    const response = await apiClient.post<{ success: boolean; conversationsAffected?: number }>(
      `/customers/${customerId}/conversations/pause`,
      { dealershipId },
    )
    return response ?? { success: true }
  } catch (error: any) {
    console.error('Failed to pause communication:', error)
    throw new Error(error.response?.data?.message || error.message || 'Failed to pause communication')
  }
}

/**
 * Stop all AI communication for a customer (all conversations). Permanent; cannot be resumed.
 */
export async function stopCustomerCommunication(
  dealershipId: string,
  customerId: string,
): Promise<{ success: boolean; conversationsAffected?: number }> {
  try {
    const response = await apiClient.post<{ success: boolean; conversationsAffected?: number }>(
      `/customers/${customerId}/conversations/stop`,
      { dealershipId },
    )
    return response ?? { success: true }
  } catch (error: any) {
    console.error('Failed to stop communication:', error)
    throw new Error(error.response?.data?.message || error.message || 'Failed to stop communication')
  }
}

/**
 * Restart AI communication for a customer (previously paused conversations only).
 */
export async function restartCustomerCommunication(
  dealershipId: string,
  customerId: string,
): Promise<{ success: boolean; conversationsAffected?: number }> {
  try {
    const response = await apiClient.post<{ success: boolean; conversationsAffected?: number }>(
      `/customers/${customerId}/conversations/restart`,
      { dealershipId },
    )
    return response ?? { success: true }
  } catch (error: any) {
    console.error('Failed to restart communication:', error)
    throw new Error(error.response?.data?.message || error.message || 'Failed to restart communication')
  }
}

/**
 * Get leads/appointments data for the dashboard table
 */
export async function getLeadsAndAppointments(
  dealershipId: string,
  filter?: 'all' | 'sold' | 'completed' | 'missed',
): Promise<{ leads: Lead[]; total: number }> {
  try {
    // Always filter for customers with appointments on the appointments page
    // Include appointments in the query to avoid N+1 queries
    const { conversations } = await getConversations(dealershipId, {
      limit: 100,
      hasAppointments: true, // Always filter for customers with appointments
      includeAppointments: true, // Include appointments in the response
    })

    // Build appointments map from included appointments (no additional API calls needed)
    const appointmentsMap = new Map<string, Appointment>()
    conversations.forEach(conv => {
      // Check if appointments are included in the customer data
      const customer = (conv as any).customer
      if (customer?.appointments && customer.appointments.length > 0) {
        // Map the appointment to the Appointment interface
        const appointment = customer.appointments[0]
        appointmentsMap.set(conv.customerId, {
          id: appointment.id,
          customerId: appointment.customerId || conv.customerId,
          type: appointment.type,
          scheduledAt: appointment.scheduledAt,
          duration: appointment.duration,
          dealershipId: appointment.dealershipId,
          salesRepId: appointment.salesRepId,
          status: appointment.status,
          notes: appointment.notes,
          createdAt: appointment.createdAt,
          updatedAt: appointment.updatedAt,
        })
      }
    })

    // Filter out conversations that don't have appointments (shouldn't happen with hasAppointments: true, but safety check)
    const conversationsWithAppointments = conversations.filter(conv => {
      const customer = (conv as any).customer
      return customer?.appointments && customer.appointments.length > 0
    })

    // Transform conversations to leads (only those with appointments)
    const leads: Lead[] = conversationsWithAppointments
      .map(conv => {
        // Get appointment data from the appointments map
        const appointment = appointmentsMap.get(conv.customerId)

        // Safety check: if we filtered for appointments but don't have one, skip this conversation
        if (!appointment) {
          console.warn(`Conversation ${conv.id} has no appointment despite hasAppointments filter`)
          return null as any
        }

        // Extract vehicle interest from appointment notes
        let vehicleInterest = conv.vehicleInterest
        if (appointment.notes) {
          try {
            // Parse notes to extract vehicle from preferences JSON
            const match = appointment.notes.match(/Preferences: ({.*})/)
            if (match) {
              const preferences = JSON.parse(match[1])
              vehicleInterest = preferences?.vehicleInterests?.primary || vehicleInterest
            }
          } catch (e) {
            // Silently handle parsing errors
          }
        }

        // Determine if this is a sold customer based on recoveryContext.type
        // This is independent of appointment status - a customer can be marked as sold
        // even if their status shows something else
        let isSold = false
        if (appointment.recoveryContext && typeof appointment.recoveryContext === 'object') {
          const context = appointment.recoveryContext as any
          isSold = context.type === 'SOLD'
        }

        return {
          id: conv.id,
          customerName: conv.customerName,
          customerPhone: conv.customerPhone,
          customerEmail: conv.customerEmail,
          leadSource: conv.leadSource,
          vehicle: vehicleInterest,
          appointmentTime: appointment.scheduledAt,
          appointmentStatus: appointment.status,
          totalMessages: conv.messageCount,
          isSold,
          conversationId: conv.id,
          customerId: conv.customerId,
          createdAt: conv.startedAt,
        }
      })
      .filter((lead): lead is Lead => lead !== null && lead !== undefined) // Filter out null entries

    // Define status groups for filtering
    const SHOWROOM_VISIT_STATUSES = ['COMPLETED', 'SHOWN_UNSOLD', 'UNSOLD_RECOVERY_PENDING', 'UNSOLD_RECOVERY_IN_PROGRESS']
    const MISSED_STATUSES = ['NO_SHOW', 'MISSED_RECOVERY_PENDING', 'MISSED_RECOVERY_IN_PROGRESS']

    // Apply additional frontend filters based on tab
    // Backend already filters to only include leads with appointments
    let filteredLeads = leads
    if (filter === 'sold') {
      // Sold: customers with recoveryContext.type === 'SOLD'
      filteredLeads = leads.filter(l => l.isSold)
    } else if (filter === 'completed') {
      // Completed: all customers who visited the showroom (any showroom visit status OR sold)
      filteredLeads = leads.filter(
        l => (l.appointmentStatus && SHOWROOM_VISIT_STATUSES.includes(l.appointmentStatus)) || l.isSold,
      )
    } else if (filter === 'missed') {
      // Missed/No Show: customers who missed their appointment or are in missed recovery
      filteredLeads = leads.filter(l => l.appointmentStatus && MISSED_STATUSES.includes(l.appointmentStatus))
    } else {
      // All Appointments: show all leads with appointments (already filtered by backend)
      filteredLeads = leads
    }

    return {
      leads: filteredLeads,
      total: filteredLeads.length,
    }
  } catch (error: any) {
    console.error('Failed to fetch leads:', error)
    throw new Error(error.response?.data?.error || error.message || 'Failed to fetch leads')
  }
}

// ===== HELPER FUNCTIONS =====

/**
 * Maps a conversation from the API response to the frontend Conversation type
 */
function mapConversationFromApi(
  conv: any,
  dealershipId: string,
  includeCustomerData?: boolean,
): Conversation & { customer?: any } {
  const customer = conv.customer

  return {
    id: conv.id,
    dealershipId: conv.dealershipId || dealershipId,
    customerId: conv.customerId,
    customerName: formatCustomerName(customer),
    customerPhone: customer?.phone,
    customerEmail: customer?.email,
    channel: conv.channel || 'SMS',
    status: (conv.status || 'ACTIVE') as ConversationStatus,
    stopCategory: (() => {
      if (conv.status !== 'TRANSFERRED') {
        return 'NONE' as ConversationStopCategory
      }
      if (conv.stopCategory === 'CRM_STOP' || conv.stopCategory === 'UNREACHABLE' || conv.stopCategory === 'ESCALATED') {
        return conv.stopCategory
      }
      return 'ESCALATED' as ConversationStopCategory
    })(),
    escalationSource: typeof conv.escalationSource === 'string' ? conv.escalationSource : undefined,
    unreachableChannels: Array.isArray(conv.unreachableChannels)
      ? conv.unreachableChannels.filter((ch: string) => ch === 'SMS' || ch === 'EMAIL')
      : undefined,
    currentWorkflow: conv.currentWorkflow,
    workflowStep: conv.workflowStep,
    vehicleInterest: formatVehicleInterest(customer, conv),
    vehicleInterestDetails: getVehicleInterestDetails(customer, conv),
    leadSource: customer?.leadSource,
    appointmentStatus: determineAppointmentStatus(conv),
    aiActive: conv.status === 'ACTIVE' && !conv.agentAssigned,
    agentAssigned: conv.agentAssigned,
    startedAt: conv.createdAt || new Date().toISOString(),
    lastMessageAt: conv.lastActivity || conv.updatedAt || new Date().toISOString(),
    messageCount: conv.messageCount || 0,
    conversationSummary: conv.conversationSummary,
    workflowSummary: conv.workflowSummary,
    summaryGeneratedAt: conv.summaryGeneratedAt,
    summaryPostedToCrm: conv.summaryPostedToCrm,
    summaryPostedAt: conv.summaryPostedAt,
    ...extractPrequalificationData(conv),
    customerExternalEvents: normalizeCustomerExternalEvents(conv.customer?.externalEvents),
    // Preserve customer data with appointments if needed (for getLeadsAndAppointments)
    ...(includeCustomerData && customer ? { customer } : {}),
  }
}

function normalizeCustomerExternalEvents(raw: unknown): CustomerExternalEvent[] {
  if (!Array.isArray(raw)) {
    return []
  }
  return raw.map((e: any) => ({
    id: e.id,
    createdAt: typeof e.createdAt === 'string' ? e.createdAt : new Date(e.createdAt).toISOString(),
    updatedAt: typeof e.updatedAt === 'string' ? e.updatedAt : new Date(e.updatedAt).toISOString(),
    dealershipId: e.dealershipId,
    customerId: e.customerId,
    eventProvider: e.eventProvider,
    integrationConnector: e.integrationConnector ?? '',
    externalId: e.externalId,
    category: e.category,
    occurredAt:
      e.occurredAt == null ? null : typeof e.occurredAt === 'string' ? e.occurredAt : new Date(e.occurredAt).toISOString(),
    ingestedAt: typeof e.ingestedAt === 'string' ? e.ingestedAt : new Date(e.ingestedAt).toISOString(),
    subject: e.subject ?? null,
    body: e.body ?? null,
    direction: e.direction ?? null,
    metadata: e.metadata && typeof e.metadata === 'object' ? (e.metadata as Record<string, unknown>) : null,
    providerLeadConsent:
      e.providerLeadConsent === 'granted' || e.providerLeadConsent === 'not_granted' || e.providerLeadConsent === 'missing'
        ? e.providerLeadConsent
        : undefined,
  }))
}

/**
 * Formats customer name from firstName and lastName
 */
function formatCustomerName(customer: any): string {
  if (!customer) return 'Unknown'
  const name = `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
  return name || 'Unknown'
}

/**
 * Resolves raw vehicle interest object. Prefers discovery context when available so we get
 * enriched fields (color, interiorColor, listedPrice, trim, etc.). Falls back to customer
 * desiredVehicles/vehicleInterests (DB) which often lack those fields.
 * Backend stores session in conversation.contextData with discoveryContext at top level.
 */
function getVehicleInterestRaw(customer: any, conv?: any): any {
  const goals = conv?.contextData?.discoveryContext?.goals ?? conv?.contextData?.sessionData?.discoveryContext?.goals
  const vehicleGoal = Array.isArray(goals) ? goals.find((g: any) => g.id === 'vehicle_of_interest') : null
  const fromGoal = vehicleGoal?.data
  const fromCustomer = customer?.desiredVehicles?.[0] || customer?.vehicleInterests?.[0]
  // Prefer goal (enriched from inventory) when it has data; otherwise use customer record
  if (fromGoal && typeof fromGoal === 'object' && Object.keys(fromGoal).length > 0) return fromGoal
  return fromCustomer ?? undefined
}

/**
 * Formats vehicle interest string from vehicle data (year, make, model; stock number when available).
 */
function formatVehicleInterest(customer: any, conv?: any): string | undefined {
  const vehicleInterest = getVehicleInterestRaw(customer, conv)
  if (!vehicleInterest) return undefined
  const year = vehicleInterest.yearMin ?? vehicleInterest.year ?? ''
  const make = vehicleInterest.make ?? ''
  const model = vehicleInterest.model ?? ''
  const base = `${year} ${make} ${model}`.trim()
  const stock = vehicleInterest.stockNumber ?? vehicleInterest.stock_number ?? ''
  if (stock) {
    return base ? `${base} (Stock #${stock})` : `Stock #${stock}`
  }
  return base || undefined
}

/**
 * Builds normalized vehicle interest details for tooltip (enriched data when available).
 */
function getVehicleInterestDetails(customer: any, conv?: any): VehicleInterestDetails | undefined {
  const raw = getVehicleInterestRaw(customer, conv)
  if (!raw) return undefined
  const attrs = raw.attributes && typeof raw.attributes === 'object' ? raw.attributes : {}
  const year = raw.yearMin ?? raw.year
  const listedPrice = raw.listedPrice ?? (typeof raw.price === 'number' ? raw.price : undefined)
  const details: VehicleInterestDetails = {}
  if (year != null) details.year = Number(year)
  if (raw.make) details.make = raw.make
  if (raw.model) details.model = raw.model
  if (raw.trim) details.trim = raw.trim
  if (raw.stockNumber ?? raw.stock_number) details.stockNumber = raw.stockNumber ?? raw.stock_number
  if (raw.vin) details.vin = raw.vin
  if (raw.mileage != null || raw.odometer != null) details.mileage = Number(raw.mileage ?? raw.odometer)
  if (raw.color ?? raw.exteriorColor ?? attrs.exteriorColor)
    details.exteriorColor = raw.color ?? raw.exteriorColor ?? attrs.exteriorColor
  if (raw.interiorColor ?? attrs.interiorColor) details.interiorColor = raw.interiorColor ?? attrs.interiorColor
  if (listedPrice != null) details.listedPrice = Number(listedPrice)
  if (raw.vehicleType) details.vehicleType = raw.vehicleType
  if (raw.description) details.description = raw.description
  if (raw.style ?? attrs.style) details.style = raw.style ?? attrs.style
  if (raw.transmission ?? attrs.transmission) details.transmission = raw.transmission ?? attrs.transmission
  if (raw.drivetrain ?? attrs.drivetrain) details.drivetrain = raw.drivetrain ?? attrs.drivetrain
  if (raw.engine ?? attrs.engine) details.engine = raw.engine ?? attrs.engine
  if (raw.engineType ?? attrs.engineType) details.engineType = raw.engineType ?? attrs.engineType
  if (raw.vehicleClass ?? attrs.vehicleClass) details.vehicleClass = raw.vehicleClass ?? attrs.vehicleClass
  if (raw.bodyType ?? attrs.bodyType) details.bodyType = raw.bodyType ?? attrs.bodyType
  if (raw.fuelType ?? attrs.fuelType) details.fuelType = raw.fuelType ?? attrs.fuelType
  if (raw.equipment ?? attrs.equipment)
    details.equipment =
      typeof raw.equipment === 'string' ? raw.equipment : typeof attrs.equipment === 'string' ? attrs.equipment : undefined
  if (Array.isArray(raw.features) && raw.features.length > 0) details.features = raw.features
  if (Object.keys(details).length === 0) return undefined
  return details
}

/**
 * Extracts prequalification data from conversation context
 */
function extractPrequalificationData(conv: any): {
  prequalStatus?: PrequalStatus
} {
  const prequalInvite = conv.contextData?.prequalInvite
  if (!prequalInvite) return {}

  const prequalStatus = prequalInvite.status as PrequalStatus | undefined

  return prequalStatus ? { prequalStatus } : {}
}

const STAFF_CHANNEL: Record<string, string> = { Text: 'SMS', Email: 'EMAIL', Phone: 'PHONE' }

/**
 * Interleave the conversation's staff messages (dealership staff and the customer, from the CRM) into its
 * thread by time. They are marked so the thread can show them apart from BDC AI's own messages.
 */
function withStaffMessages(messages: Message[], staffMessages: any[] | undefined, conversationId: string): Message[] {
  if (!staffMessages?.length) return messages
  const staff: Message[] = staffMessages.map((m: any, index: number) => ({
    id: `staff-${conversationId}-${index}`,
    conversationId,
    role: 'STAFF',
    content: [m.subject, m.body].filter(Boolean).join('\n\n') || (m.type === 'Phone' ? 'Phone call logged' : ''),
    channel: STAFF_CHANNEL[m.type] ?? 'SMS',
    direction: m.direction === 'Incoming' ? 'inbound' : 'outbound',
    status: 'delivered',
    timestamp: m.occurredAt,
    metadata: { staffMessage: true },
  }))
  return [...messages, ...staff].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
}

/**
 * Maps messages from API response to frontend Message type
 */
function mapMessagesFromApi(messages: any[], conversationId: string, defaultChannel?: string): Message[] {
  return messages.map((msg: any) => ({
    id: msg.id,
    conversationId,
    role: msg.role,
    content: msg.content,
    channel: msg.channel || defaultChannel || 'SMS',
    direction: msg.role === 'USER' ? 'inbound' : 'outbound',
    status: msg.status || 'delivered',
    timestamp: msg.createdAt,
    processedAt: msg.processedAt ?? null,
    mediaUrls: msg.metadata?.mediaUrls,
    metadata: {
      aiGenerated: msg.role === 'ASSISTANT',
      crmUpdated: msg.metadata?.crmUpdated,
      messageType: msg.metadata?.messageType,
      deliveryBlocked: msg.metadata?.deliveryBlocked,
      blockReason: msg.metadata?.blockReason,
      blockReasonLabel: msg.metadata?.blockReasonLabel,
      blockReasonCode: msg.metadata?.blockReasonCode,
      twilioStatus: msg.metadata?.twilioStatus,
      twilioErrorCode: msg.metadata?.twilioErrorCode,
      systemGenerated: msg.metadata?.systemGenerated,
      escalationReason: msg.metadata?.escalationReason,
      escalationCategory: msg.metadata?.escalationCategory,
    },
  }))
}

function determineAppointmentStatus(conv: any): string {
  // Get the actual appointment status from the customer's appointments
  const appointmentStatus = conv.customer?.appointments?.[0]?.status

  // Return the actual status if it exists, otherwise 'NO_APPOINTMENT'
  return appointmentStatus || 'NO_APPOINTMENT'
}
